import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Globe, Copy, CircleNotch } from "@phosphor-icons/react";

const label = { color: "var(--bk-text-2)" };

export function BookingPageManager({ businessId }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waitlist, setWaitlist] = useState([]);

  const load = () => {
    api.get(`/businesses/${businessId}/wellness/booking-page`).then((r) => setCfg(r.data)).catch(() => setCfg({ error: true }));
    api.get(`/businesses/${businessId}/wellness/waitlist`).then((r) => setWaitlist(r.data.entries)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const save = async (over = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/wellness/booking-page`, {
        enabled: cfg.enabled, safety_text: cfg.safety_text, hours_start: cfg.hours_start, hours_end: cfg.hours_end,
        cancel_window_hours: cfg.cancel_window_hours, timezone: cfg.timezone, waitlist_offer_hours: cfg.waitlist_offer_hours, ...over,
      });
      setCfg(data);
      if (over.enabled === undefined) toast.success("Settings saved.");
      else toast.success(data.enabled ? "Booking page is live." : "Booking page turned off.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  if (!cfg) return <p className="text-sm" style={label} data-testid="booking-page-loading">Loading…</p>;
  if (cfg.error) return <p className="text-sm" style={label} data-testid="booking-page-error">Couldn't load the booking page settings.</p>;

  const publicUrl = cfg.url_path ? `${window.location.origin}${cfg.url_path}` : null;

  return (
    <div className="space-y-5" data-testid="booking-page-manager">
      <div className="bk-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Globe size={22} weight="duotone" color="var(--bk-accent)" className="mt-0.5" />
            <div>
              <p className="font-semibold" style={{ color: "var(--bk-text)" }}>Client booking page</p>
              <p className="text-sm mt-0.5 max-w-xl" style={label}>A public page where clients see your services, prices, and open slots, confirm the safety acknowledgment, and book themselves in — sessions come off their packages automatically when you mark them completed.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" style={{ color: "var(--bk-text)" }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} data-testid="booking-page-toggle" />
            {cfg.enabled ? "Live" : "Off"}
          </label>
        </div>
        {cfg.enabled && publicUrl && (
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="booking-page-link-row">
            <code className="text-xs px-3 py-2 rounded-md border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} data-testid="booking-page-url">{publicUrl}</code>
            <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied."); }} data-testid="booking-page-copy"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
              <Copy size={13} weight="bold" /> Copy link
            </button>
            <a href={cfg.url_path} target="_blank" rel="noopener noreferrer" data-testid="booking-page-open"
              className="px-3 py-1.5 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>Open page</a>
          </div>
        )}
      </div>

      <div className="bk-card p-5 space-y-3">
        <p className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>Booking hours</p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="time" value={cfg.hours_start} onChange={(e) => setCfg((c) => ({ ...c, hours_start: e.target.value }))} data-testid="booking-hours-start"
            className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <span className="text-sm" style={label}>to</span>
          <input type="time" value={cfg.hours_end} onChange={(e) => setCfg((c) => ({ ...c, hours_end: e.target.value }))} data-testid="booking-hours-end"
            className="rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <span className="text-sm ml-3" style={label}>Self-cancel/reschedule window:</span>
          <input type="number" min={0} max={168} value={cfg.cancel_window_hours} onChange={(e) => setCfg((c) => ({ ...c, cancel_window_hours: Number(e.target.value) }))} data-testid="booking-cancel-window"
            className="w-20 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <span className="text-sm" style={label}>hours before the slot</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm" style={label}>Business time zone:</span>
          <select value={cfg.timezone} onChange={(e) => setCfg((c) => ({ ...c, timezone: e.target.value }))} data-testid="booking-timezone"
            className="rounded-md border px-3 py-2 text-sm bg-white outline-none" style={{ borderColor: "var(--bk-border)" }}>
            {["America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"].map((z) => (
              <option key={z} value={z}>{z.replace("America/", "").replace("Pacific/", "").replace("_", " ")}</option>
            ))}
          </select>
          <span className="text-sm ml-3" style={label}>Waitlist offers expire after:</span>
          <input type="number" min={1} max={72} value={cfg.waitlist_offer_hours} onChange={(e) => setCfg((c) => ({ ...c, waitlist_offer_hours: Number(e.target.value) }))} data-testid="booking-waitlist-hours"
            className="w-20 rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
          <span className="text-sm" style={label}>hours</span>
        </div>
        <p className="font-semibold text-sm pt-2" style={{ color: "var(--bk-text)" }}>Safety acknowledgment (clients must accept before booking)</p>
        <textarea value={cfg.safety_text} onChange={(e) => setCfg((c) => ({ ...c, safety_text: e.target.value }))} data-testid="booking-safety-text"
          rows={4} className="w-full rounded-md border px-3 py-2 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
        <div className="flex justify-end">
          <button onClick={() => save()} disabled={saving} data-testid="booking-page-save"
            className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-70">
            {saving ? <CircleNotch size={15} className="animate-spin" /> : null} Save settings
          </button>
        </div>
      </div>

      {waitlist.length > 0 && (
        <div className="bk-card p-5" data-testid="waitlist-owner-card">
          <p className="font-semibold text-sm mb-3" style={{ color: "var(--bk-text)" }}>Waitlist ({waitlist.length})</p>
          <div className="space-y-2">
            {waitlist.map((w) => (
              <div key={w.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`waitlist-entry-${w.id}`}>
                <span style={{ color: "var(--bk-text)" }}>
                  <span className="font-semibold">{w.client_name}</span> — {w.service_name} on {w.date}
                </span>
                <span className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
                  style={{ background: w.status === "waiting" ? "var(--bk-rowan-bg)" : w.status === "offered" ? "#FFF3DC" : w.status === "booked" ? "var(--bk-success-bg)" : "#F3F1EC",
                           color: w.status === "waiting" ? "var(--bk-accent)" : w.status === "offered" ? "#B08840" : w.status === "booked" ? "var(--bk-success)" : "var(--bk-text-2)" }}>
                  {w.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
